<script setup>
import { ref, computed } from "vue";
import RegionCombo from "../components/RegionCombo.vue";
import { getApiBaseUrl } from "../components/ApiBaseUrl.js";
import * as XLSX from "xlsx";

const region = ref("");
const yyyymm = ref(new Date().toISOString().slice(0, 7).replace("-", "")); // 예 "202601"
const keyword = ref("");
const priceMin = ref("10.0");
const priceMax = ref("15.0");

const loading = ref(false);
const error = ref("");
const deals = ref([]);

// sorting
const sortKey = ref(""); // e.g. 'apt','dongfloor','area','price','date','road'
const sortDir = ref(1); // 1 = asc, -1 = desc

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = -sortDir.value;
  } else {
    sortKey.value = key;
    sortDir.value = 1;
  }
}

function getValue(item, key) {
  const v = item[key];
  if (key === "price" || key === "area") {
    // try to parse as number (may be string like "12.5")
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : String(v || "").toLowerCase();
  }
  return String(v || "").toLowerCase();
}

const sortedDeals = computed(() => {
  const arr = (deals.value || []).slice();
  if (!sortKey.value) return arr;
  arr.sort((a, b) => {
    const va = getValue(a, sortKey.value);
    const vb = getValue(b, sortKey.value);
    if (va < vb) return -1 * sortDir.value;
    if (va > vb) return 1 * sortDir.value;
    return 0;
  });
  return arr;
});

const API_BASE = getApiBaseUrl();

function computeDateRangeFromYYYYMM(yyyymmStr) {
  const s = String(yyyymmStr || "").trim();
  // If user provided only a 4-digit year (e.g. "2025"), return full year range
  if (/^\d{4}$/.test(s)) {
    const year = Number(s);
    const start = `${s}-01-01`;
    const end = `${year + 1}-01-01`;
    return { start, end };
  }

  // Fallback: treat as YYYYMM (e.g. "202501")
  const year = Number(s.slice(0, 4)) || new Date().getFullYear();
  const month = Number(s.slice(4, 6)) || 1;
  const start = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
  const next = new Date(year, month, 1); // next month
  const endYear = next.getFullYear();
  const endMonth = String(next.getMonth() + 1).padStart(2, "0");
  const end = `${endYear}-${endMonth}-01`;
  return { start, end };
}

async function fetchDeals() {
  loading.value = true;
  error.value = "";
  deals.value = [];

  try {
    const { start, end } = computeDateRangeFromYYYYMM(yyyymm.value);
    const body = {
      district: region.value || "",
      minPrice: priceMin.value || "10",
      maxPrice: priceMax.value || "15",
      startDate: start,
      endDate: end,
      keyword: keyword.value || "",
    };

    const res = await fetch(`${API_BASE}/api/query-deals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error("network");
    const data = await res.json();
    if (!data.ok) {
      error.value = data.message || "서버 오류";
      return;
    }

    // Map DB rows to view model
    deals.value = (data.rows || []).map((r, idx) => ({
      id: idx + 1,
      apt: r.단지명 || r.단지 || "",
      district: r.구군 || "",
      road: r.도로명 || "",
      area: r.전용면적_평 || "",
      price: r.거래금액_억 || r.거래금액＿억 || "",
      dongfloor: (r.동 || "") + " " + (r.층 || "") + "층",
      date: r.계약일 || "",
    }));
  } catch (e) {
    console.error("fetchDeals error", e);
    error.value = "조회 중 오류가 발생했습니다.";
  } finally {
    loading.value = false;
  }
}

function exportExcel() {
  try {
    if (!deals.value || !deals.value.length) {
      alert("조회 결과가 없습니다.");
      return;
    }

    // Prepare data rows with Korean headers
    const headers = [
      "단지명",
      "동/층",
      "전용면적(평)",
      "거래금액(억)",
      "계약일",
      "구군",
      "도로명",
    ];

    const fields = [
      "apt",
      "dongfloor",
      "area",
      "price",
      "date",
      "district",
      "road",
    ];

    const aoa = [headers].concat(
      deals.value.map((d) => fields.map((f) => d[f] ?? "")),
    );

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Deals");

    // Use writeFile to trigger download in browser
    XLSX.writeFile(wb, "부동산실거래가.xlsx");
  } catch (e) {
    console.error("exportExcel error", e);
    alert("엑셀(.xlsx) 내보내기 중 오류가 발생했습니다.");
  }
}
</script>

<template>
  <div class="wrap">
    <div class="panel">
      <h2>부동산 실거래가 조회</h2>
      <div class="filters">
        <label>
          지역
          <RegionCombo v-model="region" />
        </label>
        <label>
          실거래가(억)
          <div style="display: flex; gap: 4px; width: 100%">
            <input
              id="priceMin"
              v-model="priceMin"
              placeholder="예: 10.0"
              style="flex: 1; width: 40%"
            />
            <input
              id="priceMax"
              v-model="priceMax"
              placeholder="예: 15.0"
              style="flex: 1; width: 40%"
            />
          </div>
        </label>
        <label>
          기준월(YYYYMM)
          <input
            id="yyyymm"
            v-model="yyyymm"
            placeholder="예: 202601"
            style="flex: 1; width: 60%"
          />
        </label>

        <label>
          키워드(아파트명)
          <input id="keyword" v-model="keyword" placeholder="예: 래미안" />
        </label>

        <div style="display: flex; gap: 8px; align-items: center">
          <button
            class="btn"
            @click="fetchDeals"
            :disabled="loading"
            style="width: 50%"
          >
            {{ loading ? "조회중..." : "조회" }}
          </button>
          <button
            class="btn"
            @click="exportExcel"
            :disabled="loading"
            style="width: 50%"
          >
            Excel
          </button>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="list">
        <!-- 헤더 -->
        <div class="row head">
          <span class="clickable" @click="setSort('apt')">
            단지명<span v-if="sortKey === 'apt'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('dongfloor')">
            동/층<span v-if="sortKey === 'dongfloor'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('area')">
            전용면적(평)<span v-if="sortKey === 'area'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('price')">
            거래금액(억)<span v-if="sortKey === 'price'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('date')">
            계약일<span v-if="sortKey === 'date'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('district')">
            구군<span v-if="sortKey === 'district'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('road')">
            도로명<span v-if="sortKey === 'road'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
        </div>

        <!-- 본문: 각 행 + (바로 아래) 상세 -->
        <template v-if="sortedDeals.length">
          <div class="group" v-for="d in sortedDeals" :key="d.id">
            <div class="row item" role="button" tabindex="0">
              <span>{{ d.apt }}</span>
              <span>{{ d.dongfloor }}</span>
              <span>{{ d.area }}</span>
              <span>{{ d.price }}</span>
              <span>{{ d.date }}</span>
              <span>{{ d.district }}</span>
              <span>{{ d.road }}</span>
            </div>
          </div>
        </template>

        <p v-else class="empty">조회 결과가 없습니다.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wrap {
  max-width: 99.5%;
}
.panel {
  padding: 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.filters {
  display: grid;
  grid-template-columns: 0.5fr repeat(4, 1fr);
  gap: 10px;
  align-items: end;
  margin-top: 12px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  opacity: 0.95;
}
input {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.18);
  color: #eaf2ff;
}
.btn {
  height: 40px;
  border-radius: 12px;
  border: 0;
  background: #2d8cff;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.error {
  color: #ffb3b3;
  margin-top: 10px;
}
.list {
  margin-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 570px;
  overflow-y: auto;
}
.row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 0.8fr 0.8fr 0.6fr 1.4fr;
  gap: 10px;
  padding: 10px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.head {
  opacity: 0.8;
  font-weight: 700;
}
.clickable {
  cursor: pointer;
  user-select: none;
}
.price {
  font-weight: 900;
  color: #bfe0ff;
  text-align: center;
}
.empty {
  margin-top: 14px;
  opacity: 0.8;
}
@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr 1fr;
  }
  .row {
    grid-template-columns: 2fr 1fr 1fr 0.8fr;
  }
  /* hide less important columns on small screens: 동/층, 계약일, 도로명 */
  .row span:nth-child(2),
  .row span:nth-child(5),
  .row span:nth-child(7) {
    display: none;
  }
}
</style>
